import { Middleware, isRejectedWithValue, UnknownAction } from '@reduxjs/toolkit';
import { setError } from '../ErrorReducer/errorSlice';
import { handleError } from '../../utils/errorUtils';

/**
 * Error Middleware
 *
 * Centralized error handling middleware that intercepts rejected async thunk actions
 * and selectively dispatches them to the global error notification state.
 *
 * Only action type prefixes listed in GLOBAL_ERROR_ACTION_ALLOWLIST (e.g. 'user/')
 * will trigger the global error popup. All other thunk rejections (grid/data-fetch
 * actions) are intentionally ignored here and handled inline per section instead.
 *
 * Benefits:
 * - Single point of allowlist-based error routing
 * - Thunks use rejectWithValue to preserve raw error data
 * - Clean separation: auth errors show a popup, data errors show inline messages
 *
 * @returns Redux middleware function
 */
// Only actions with these type prefixes will trigger the global error notification popup.
// Grid/data-fetch thunk errors are handled inline per section - they must NOT show a popup.
const GLOBAL_ERROR_ACTION_ALLOWLIST: string[] = ['user/'];

export const errorMiddleware: Middleware = store => next => (action: UnknownAction) => {
  // Pass action to next middleware/reducer first
  const result = next(action);

  // Check if action is a rejected thunk action with value
  if (isRejectedWithValue(action)) {
    const actionType = (action as { type: string }).type;
    const isAllowlisted = GLOBAL_ERROR_ACTION_ALLOWLIST.some(prefix =>
      actionType.startsWith(prefix),
    );

    // Only show global popup for allowlisted action types (e.g. auth errors).
    // Grid/data-fetch errors are shown inline in the relevant section instead.
    if (!isAllowlisted) {
      return result;
    }

    // Transform raw axios error to standardized ErrorResponse
    const error = handleError(action.payload);

    if (error) {
      // Dispatch to global error state
      store.dispatch(setError(error));
    }
  }

  return result;
};
